import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { attachmentStorageKey, newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { areaHistory, useAreaObservations, type HistoryPhoto } from '../data/areaHistory.ts'
import { areaLabel, buildAreaTree, useAreas, useAreaVersions, type AreaNode } from '../data/geo.ts'
import { useRpc } from '../data/people.ts'
import { hasTaskTag } from '@gk/contracts'
import { settingLines, say, typeLabel, useJobs, useTaskTypes } from '../data/plan.ts'
import { useNames } from '../data/planUi.ts'
import { doneLines } from '../data/settingsCompare.ts'
import { useSettingsConfirmations } from '../data/settingsDone.ts'
import { useCompletions, useCoverage, useWorkSessions } from '../data/work.ts'
import { formatArea } from '../lib/geo.ts'
import { formatLocal } from '../lib/time.ts'
import { frameFor } from '../data/lookGeometry.ts'
import { useBlobs, type LinkRow } from '../photos/usePhotos.ts'
import type { BlobRow } from '../sync/db.ts'
import { useMerged } from '../sync/hooks.ts'
import { Banner, Field, PendingBanner, Pill } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { PlayLinePanel } from './course/PlayLinePanel.tsx'
import { DoneLinesView, SettingLinesView } from './plan/JobSettings.tsx'

interface Rules { types: Array<{ code: string; label: { fr: string; en: string }; sort: number }>; rules: Array<[string, string]> }

const matches = (n: AreaNode, q: string, lang: string): boolean =>
  areaLabel(n.area, lang).toLowerCase().includes(q) || (n.area.code ?? '').toLowerCase().includes(q) || n.children.some((c) => matches(c, q, lang))

/** The course as the head greenkeeper keeps it: site, loops, holes, surfaces, as a tree whose types and nesting are data. Nothing here knows how
 *  many holes a course has. Drawing an outline is done on the map (see the "Draw" links).
 *  M23 item 41: every row of the tree is 56 px, the whole row opens the area, the fold is a 56-px control; the level is said by the indent alone. */
export function CoursePage() {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const edit = useWriteAccess('area.edit')
  const focus = (useSearch({ strict: false }) as { area?: string }).area
  const areas = useAreas(); const versions = useAreaVersions()
  const rules = useRpc<Rules>('area_rules', {})
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [panel, setPanel] = useState<string | null>(focus ?? null)
  const [archived, setArchived] = useState(false)
  const [flash, setFlash] = useState<string | null>(null)
  const lang = i18n.language

  const tree = useMemo(() => buildAreaTree(areas ?? [], versions ?? [], lang, archived), [areas, versions, lang, archived])
  // roots and the first level are open the first time; a focused area opens its ancestors
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current || !areas || tree.length === 0) return
    seeded.current = true
    const next = new Set<string>(tree.flatMap((r) => [r.area.id, ...r.children.map((c) => c.area.id)].slice(0, 12)))
    for (let a = areas.find((x) => x.id === focus); a; a = areas.find((x) => x.id === a!.parent_id)) next.add(a.id)
    setOpen(next)
  }, [tree, areas, focus])

  if (!areas || !versions) return <p>{t('common.loading')}</p>
  const needle = q.trim().toLowerCase()
  const toggle = (id: string) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const render = (n: AreaNode): React.ReactNode => {
    if (needle && !matches(n, needle, lang)) return null
    const expanded = needle ? true : open.has(n.area.id)
    return (
      <li key={n.area.id} data-testid="area-row" data-area={n.area.id}>
        <div className={`flex min-h-14 items-center gap-1 rounded-lg ${panel === n.area.id ? 'bg-amber-50 dark:bg-slate-800' : ''}`} style={{ paddingLeft: `${n.depth * 14}px` }}>
          {n.children.length > 0
            ? <button type="button" className="grid size-14 shrink-0 place-items-center rounded-lg active:bg-slate-200 dark:active:bg-slate-700" aria-expanded={expanded} aria-label={t(expanded ? 'course.collapse' : 'course.expand')} onClick={() => toggle(n.area.id)} data-testid="area-toggle"><Picto name="chevron" className={`size-6 transition-transform ${expanded ? 'rotate-90' : ''}`} strokeWidth={2.2} /></button>
            : <span className="w-6 shrink-0" aria-hidden />}
          <button type="button" className="flex min-h-14 min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 py-1 pr-2 text-left" aria-expanded={panel === n.area.id} onClick={() => setPanel(panel === n.area.id ? null : n.area.id)} data-testid="area-open">
            <span className="font-semibold">{areaLabel(n.area, lang)}</span>
            {t(`areaType.${n.area.area_type_code}`) !== areaLabel(n.area, lang) && <span className="meta">{t(`areaType.${n.area.area_type_code}`)}</span>}
            {n.version ? <Pill tone="green">{formatArea(Number(n.version.area_m2), lang)}</Pill> : n.area.area_type_code === 'site_root' || n.children.length ? null : <Pill tone="amber">{t('course.notDrawn')}</Pill>}
            {n.area.archived_at && <Pill tone="gray">{t('course.archived')}</Pill>}
            {n.area._pending && <Pill tone="amber">{t('assets.pending')}</Pill>}
          </button>
        </div>
        {panel === n.area.id && <AreaPanel node={n} rules={rules.data} canEdit={edit.allowed} lang={lang} tz={site.timezone} onArchived={() => { setPanel(null); setFlash(t('course.archivedDone')) }} />}
        {expanded && n.children.length > 0 && <ul className="flex flex-col">{n.children.map(render)}</ul>}
      </li>
    )
  }

  return (
    <section className="flex flex-col gap-4" data-testid="course-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">{t('course.title')}</h1>
        <div className="flex gap-2">
          <Link to="/map" className="btn !min-h-11 !px-4" data-testid="go-map">{t('course.map')}</Link>
          {edit.allowed && <Link to="/course/import" className="btn !min-h-11 !px-4" data-testid="go-course-import">{t('course.import')}</Link>}
        </div>
      </div>
      <p className="meta">{t('course.help')}</p>
      {edit.readOnlyMode && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      {flash && <Banner tone="ok"><span data-testid="course-flash">{flash}</span></Banner>}
      {tree.length === 0 && (edit.permitted ? <NewRoot canEdit={edit.allowed} siteName={site.name} /> : <p className="opacity-70">{t('course.empty')}</p>)}
      {tree.length > 0 && (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('course.search')} htmlFor="course-search"><input id="course-search" type="search" className="input" value={q} onChange={(e) => setQ(e.target.value)} data-testid="course-search" /></Field>
            <label className="flex min-h-14 items-center gap-3"><input type="checkbox" className="size-6" checked={archived} onChange={(e) => setArchived(e.target.checked)} />{t('course.showArchived')}</label>
          </div>
          <ul className="flex flex-col" data-testid="area-tree">{tree.map(render)}</ul>
        </>
      )}
    </section>
  )
}

function NewRoot({ canEdit, siteName }: { canEdit: boolean; siteName: string }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const id = useRef(newId())
  return (
    <div className="card flex flex-col gap-3" data-testid="course-new-root">
      <p>{t('course.noRoot')}</p>
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error">{w.error}</Banner>}
      <button className="btn btn-primary self-start" disabled={!canEdit || w.busy} data-testid="course-create-root"
        onClick={() => void w.run('area.create@1', { area_id: id.current, area_type: 'site_root', name: { fr: siteName.slice(0, 120) } }, 'course.created')}>{t('course.createRoot', { name: siteName })}</button>
    </div>
  )
}

function AreaPanel({ node, rules, canEdit, lang, tz, onArchived }: { node: AreaNode; rules: Rules | null; canEdit: boolean; lang: string; tz: string; onArchived: () => void }) {
  const { t } = useTranslation()
  const { can } = useClub()
  const { area, version } = node
  const history = useAreaVersions()
  const own = (history ?? []).filter((v) => v.area_id === area.id).sort((a, b) => b.version_no - a.version_no)
  const w = useWorkshopAction()
  const [name, setName] = useState({ fr: area.name.fr ?? '', en: area.name.en ?? '' })
  const [code, setCode] = useState(area.code ?? '')
  const [hole, setHole] = useState(area.hole_number ? String(area.hole_number) : '')
  const [sure, setSure] = useState(false)
  const changed = name.fr !== (area.name.fr ?? '') || name.en !== (area.name.en ?? '') || code !== (area.code ?? '') || hole !== (area.hole_number ? String(area.hole_number) : '')
  const childTypes = useMemo(() => (rules?.rules ?? []).filter(([p]) => p === area.area_type_code).map(([, c]) => c), [rules, area.area_type_code])

  const save = (e: FormEvent) => {
    e.preventDefault()
    const set: Record<string, unknown> = {}
    if (name.fr !== (area.name.fr ?? '') || name.en !== (area.name.en ?? '')) set.name = { ...(name.fr.trim() ? { fr: name.fr.trim() } : {}), ...(name.en.trim() ? { en: name.en.trim() } : {}) }
    if (code !== (area.code ?? '')) set.code = code.trim() || null
    if (hole !== (area.hole_number ? String(area.hole_number) : '')) set.hole_number = hole.trim() ? Number(hole) : null
    void w.run('area.update@1', { area_id: area.id, expected_revision: area.revision, set } as never, 'course.saved', { aggregateId: area.id })
  }
  return (
    <div className="card my-2 ml-4 flex flex-col gap-3" data-testid="area-panel">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {version ? <span data-testid="area-facts">{formatArea(Number(version.area_m2), lang)} · {t('map.version', { n: version.version_no })} · {formatLocal(version.captured_on, tz, 'dd/MM/yyyy')} · {version.source === 'sketch' ? t('map.sketch') : version.accuracy_m == null ? t('map.accUnknown') : t('map.accuracy', { m: version.accuracy_m, source: t(`geoSource.${version.source}`) })}</span> : <span className="opacity-70">{t('course.notDrawn')}</span>}
        <Link to="/map" search={{ area: area.id }} className="btn !px-4" data-testid="area-on-map">{t('course.onMap')}</Link>
        {area.area_type_code !== 'site_root' && can('measurement.read') && <Link to="/measures/series" search={{ area: area.id }} className="btn !px-4" data-testid="area-measures">{t('course.measures')}</Link>}
        {canEdit && area.area_type_code !== 'site_root' && !area.archived_at && <Link to="/course/$areaId/shape" params={{ areaId: area.id }} className="btn btn-primary !px-4" data-testid="area-draw">{version ? t('course.redraw') : t('course.draw')}</Link>}
      </div>
      {own.length > 1 && (
        <details>
          <summary className="flex min-h-14 cursor-pointer items-center text-sm font-semibold">{t('course.history', { count: own.length })}</summary>
          <ul className="mt-1 text-sm" data-testid="area-history">{own.map((v) => <li key={v.id}>{t('map.version', { n: v.version_no })} · {formatArea(Number(v.area_m2), lang)} · {formatLocal(v.captured_on, tz, 'dd/MM/yyyy')} · {t(`geoSource.${v.source}`)}</li>)}</ul>
        </details>
      )}
      {/* M25-L's panel (placed by D): the outline turned to its line of play, where the line came from, « Ajuster » for area.edit; nothing for an area type with no line */}
      {area.area_type_code !== 'site_root' && <PlayLinePanel areaId={area.id} />}
      {area.area_type_code !== 'site_root' && <AreaWorkHistory areaId={area.id} areaType={area.area_type_code} tz={tz} lang={lang} />}
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error"><span data-testid="course-error">{w.error}</span></Banner>}
      {w.ok && <Banner tone="ok"><span data-testid="course-ok">{w.ok}</span></Banner>}
      {canEdit && !area.archived_at && (
        <>
          <form className="flex flex-col gap-3" onSubmit={save} data-testid="area-edit">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('course.nameFr')} htmlFor={`an-fr-${area.id}`}><input id={`an-fr-${area.id}`} className="input" maxLength={120} value={name.fr} onChange={(e) => setName({ ...name, fr: e.target.value })} data-testid="area-name-fr" /></Field>
              <Field label={t('course.nameEn')} htmlFor={`an-en-${area.id}`}><input id={`an-en-${area.id}`} className="input" maxLength={120} value={name.en} onChange={(e) => setName({ ...name, en: e.target.value })} data-testid="area-name-en" /></Field>
              <Field label={t('course.code')} htmlFor={`ac-${area.id}`}><input id={`ac-${area.id}`} className="input" maxLength={30} value={code} onChange={(e) => setCode(e.target.value)} data-testid="area-code" /></Field>
              {area.area_type_code === 'hole' && <Field label={t('course.holeNumber')} htmlFor={`ah-${area.id}`}><input id={`ah-${area.id}`} className="input" inputMode="numeric" value={hole} onChange={(e) => setHole(e.target.value.replace(/\D/g, ''))} data-testid="area-hole" /></Field>}
            </div>
            <button className="btn !min-h-11 self-start" disabled={w.busy || !changed || (!name.fr.trim() && !name.en.trim())} data-testid="area-save">{t('common.save')}</button>
          </form>
          {childTypes.length > 0 && <NewArea parent={node} types={childTypes} />}
          {area.area_type_code !== 'site_root' && (sure
            ? <button className="btn btn-danger !min-h-11 self-start" disabled={w.busy} onClick={() => void w.run('area.archive@1', { area_id: area.id }, 'course.archivedDone').then((ok) => { setSure(false); if (ok) onArchived() })} data-testid="area-archive-confirm">{t('course.archiveConfirm')}</button>
            : <button className="btn !min-h-11 self-start" onClick={() => setSure(true)} data-testid="area-archive">{t('course.archive')}</button>)}
        </>
      )}
    </div>
  )
}

function NewArea({ parent, types }: { parent: AreaNode; types: string[] }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [type, setType] = useState(types[0] ?? '')
  const [fr, setFr] = useState('')
  const [code, setCode] = useState('')
  const [hole, setHole] = useState('')
  const sort = Math.max(0, ...parent.children.map((c) => c.area.sort_key)) + 1
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void w.run('area.create@1', { area_id: id.current, parent_id: parent.area.id, area_type: type, name: { fr: fr.trim() }, sort_key: sort, ...(code.trim() ? { code: code.trim() } : {}), ...(type === 'hole' && hole ? { hole_number: Number(hole) } : {}) }, 'course.created')
      .then((ok) => { if (ok) { id.current = newId(); setFr(''); setCode(''); setHole('') } })
  }
  return (
    <form className="flex flex-col gap-3 border-t pt-3" onSubmit={submit} data-testid="area-new">
      <h3 className="font-bold">{t('course.addInside')}</h3>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('course.type')} htmlFor={`nt-${parent.area.id}`}><select id={`nt-${parent.area.id}`} className="input" value={type} onChange={(e) => setType(e.target.value)} data-testid="new-area-type">{types.map((c) => <option key={c} value={c}>{t(`areaType.${c}`)}</option>)}</select></Field>
        <Field label={t('course.nameFr')} htmlFor={`nn-${parent.area.id}`}><input id={`nn-${parent.area.id}`} className="input" maxLength={120} value={fr} onChange={(e) => setFr(e.target.value)} data-testid="new-area-name" /></Field>
        <Field label={t('course.code')} htmlFor={`nc-${parent.area.id}`}><input id={`nc-${parent.area.id}`} className="input" maxLength={30} value={code} onChange={(e) => setCode(e.target.value)} data-testid="new-area-code" /></Field>
        {type === 'hole' && <Field label={t('course.holeNumber')} htmlFor={`nh-${parent.area.id}`}><input id={`nh-${parent.area.id}`} className="input" inputMode="numeric" value={hole} onChange={(e) => setHole(e.target.value.replace(/\D/g, ''))} data-testid="new-area-hole" /></Field>}
      </div>
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error"><span data-testid="new-area-error">{w.error}</span></Banner>}
      {w.ok && <Banner tone="ok"><span data-testid="new-area-ok">{w.ok}</span></Banner>}
      <button className="btn btn-primary self-start" disabled={w.busy || !fr.trim() || !type} data-testid="new-area-go">{t('course.add')}</button>
    </form>
  )
}

/** The work shown at most on an area's page (the rest is counted: a green is mowed most days of the season). */
const MAX_SHOWN = 60
type T = (k: string, o?: Record<string, unknown>) => string
/** The kinds of a ground observation in words. Each key is a literal, so `i18nKeys.test.ts` holds it to the catalogue; a word the catalogue does not know shows as it is. */
const obsKindText = (t: T, kind: string): string =>
  ({ disease: t('course.obsKind.disease'), pest: t('course.obsKind.pest'), damage: t('course.obsKind.damage'), wet: t('course.obsKind.wet'), dry: t('course.obsKind.dry'), other: t('course.obsKind.other') } as Record<string, string>)[kind] ?? kind

/** M16-K, "Travaux sur cette zone" (docs/M16.md K, benchmark 13): the area's own history from the device's copy (`data/areaHistory.ts`): one line per finish that
 *  covered the area (its day, the kind of work, who, the settings as the daily sheet prints them, what the finish said of this area, the photos), then the
 *  observations made on it. Folded like the shape's versions, with the counts on the fold: a green carries dozens of lines a season and the direction's edit
 *  form under it must stay within reach. Reads the mirror only: it opens with no network. */
function AreaWorkHistory({ areaId, areaType, tz, lang }: { areaId: string; areaType: string; tz: string; lang: string }) {
  const areaFrame = frameFor([areaType])
  const { t } = useTranslation()
  const completions = useCompletions(); const coverage = useCoverage(); const sessions = useWorkSessions(); const jobs = useJobs(); const taskTypes = useTaskTypes()
  const links = useMerged<LinkRow>('attachment_link', 'core.attachment_link'); const observations = useAreaObservations()
  const names = useNames(); const blobs = useBlobs()
  const confirmations = useSettingsConfirmations()
  const { can, kiosk } = useClub()
  // M25-D: « prévu, non confirmé » is said only where the device holds every finish (a crew phone holds its own: it shows the plan alone)
  const reads = !kiosk && can('worklog.read')
  const history = useMemo(() => (completions && coverage && sessions && jobs && links && observations ? areaHistory(areaId, completions, coverage, sessions, jobs, links, observations, { tz, confirmations: confirmations ?? [] }) : null),
    [areaId, completions, coverage, sessions, jobs, links, observations, tz, confirmations])
  const typeByCode = useMemo(() => new Map((taskTypes ?? []).map((x) => [x.code, x])), [taskTypes])
  if (!history) return <p className="meta">{t('common.loading')}</p>
  const who = (ids: readonly string[]) => ids.map((id) => names?.get(id) ?? '?').join(', ')
  const shown = history.work.slice(0, MAX_SHOWN)
  const frame = areaFrame
  const thumbs = (photos: readonly HistoryPhoto[]) => photos.length > 0 && <ul className="flex flex-wrap gap-2" data-testid="history-photos">{photos.map((p) => <li key={p.id}><HistoryThumb photo={p} blob={blobs.get(p.sha256)} /></li>)}</ul>
  return (
    <details data-testid="area-work" data-count={history.work.length} data-observations={history.observations.length}>
      <summary className="flex min-h-14 cursor-pointer items-center text-sm font-semibold" data-testid="area-work-toggle">
        {t('course.workTitle')} · {t('course.workCount', { count: history.work.length })} · {t('course.obsCount', { count: history.observations.length })}
      </summary>
      <div className="flex flex-col gap-3 pt-1">
        <p className="meta">{t('course.workHelp', { days: history.days })}</p>
        {shown.length === 0 && <p className="meta" data-testid="area-work-none">{t('course.workNone', { days: history.days })}</p>}
        {shown.length > 0 && (
          <ul className="flex flex-col gap-2">
            {shown.map((w) => {
              const tt = typeByCode.get(w.taskTypeCode); const label = typeLabel(tt, w.taskTypeCode, lang); const title = say(w.title, lang); const lines = settingLines(w.params, tt, lang)
              return (
                <li key={w.id} className="flex flex-col gap-1 border-t border-slate-200 pt-2 dark:border-night-line" data-testid="area-work-row" data-job={w.jobId} data-coverage={w.coverage}>
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{formatLocal(w.at, tz, 'dd/MM/yyyy')}</span>
                    <span>{label}{title && title !== label ? ` · ${title}` : ''}</span>
                    <Pill tone={w.coverage === 'full' ? 'green' : 'amber'}>{w.coverage === 'full' ? t('course.coverageFull') : t('course.coveragePartial')}</Pill>
                    {w.pending && <Pill tone="amber">{t('assets.pending')}</Pill>}
                  </p>
                  <p className="meta">{who(w.personIds)}</p>
                  {lines.length > 0 && <SettingLinesView lines={lines} frame={frame} className="text-sm" testId="area-work-settings" />}
                  {w.confirmation
                    ? <div className="flex flex-col gap-1" data-testid="area-work-done"><p className="meta font-semibold">{t('plan.settingsView.done')}</p><DoneLinesView lines={doneLines(w.params, tt, w.confirmation, lang)} frame={frame} /></div>
                    : reads && lines.length > 0 && hasTaskTag(tt, 'confirm_settings') && <p className="meta" data-testid="area-work-unconfirmed">{t('plan.settingsView.unconfirmed')}</p>}
                  {w.notes && <p className="meta italic">{w.notes}</p>}
                  {thumbs(w.photos)}
                </li>
              )
            })}
          </ul>
        )}
        {history.work.length > MAX_SHOWN && <p className="meta" data-testid="area-work-more">{t('course.moreWork', { count: history.work.length - MAX_SHOWN })}</p>}
        <h4 className="font-bold">{t('course.obsTitle')}</h4>
        {history.observations.length === 0 && <p className="meta" data-testid="area-obs-none">{t('course.obsNone', { days: history.days })}</p>}
        {history.observations.length > 0 && (
          <ul className="flex flex-col gap-2">
            {history.observations.map((o) => (
              <li key={o.id} className="flex flex-col gap-1 border-t border-slate-200 pt-2 dark:border-night-line" data-testid="area-observation" data-kind={o.kind} data-job={o.jobId ?? ''}>
                <p className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{formatLocal(o.at, tz, 'dd/MM/yyyy HH:mm')}</span>
                  <span>{obsKindText(t, o.kind)}</span>
                  {!o.jobId && <Pill tone="amber">{t('course.obsNoJob')}</Pill>}
                  {o.pending && <Pill tone="amber">{t('assets.pending')}</Pill>}
                </p>
                {o.note && <p>{o.note}</p>}
                <p className="meta">{t('history.by', { who: (o.personId && names?.get(o.personId)) || '?' })}</p>
                {thumbs(o.photos)}
                {o.jobId && <Link to="/plan/job/$jobId" params={{ jobId: o.jobId }} className="btn !px-4 self-start" data-testid="area-observation-job">{t('course.obsJob')}</Link>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  )
}

const THUMB = 'size-20 rounded-lg border-2 border-slate-300 object-cover dark:border-slate-600'
/** A photo of the history, shown as `PhotoStrip` shows one (photos/PhotoField.tsx's `LinkThumb`, kept apart at Z's close: this one carries the history's own test
 *  ids and its `data-sha` on the image, which the page test and course.spec pin, and no pending pill; a shared thumbnail with configurable ids is a later review's):
 *  this device's own copy while the server has not stored the bytes, the signed /files address once online, a placeholder meanwhile. The links come from ONE
 *  read of the mirror (`areaHistory`), never one live query per picture. */
function HistoryThumb({ photo, blob }: { photo: HistoryPhoto; blob: BlobRow | undefined }) {
  const { t } = useTranslation()
  const { transport, org } = useClub()
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let live = true; let revoke: string | null = null
    if (blob) { revoke = URL.createObjectURL(blob.blob); setUrl(revoke) }
    else {
      setUrl(null)
      void transport.attachmentUrl(attachmentStorageKey(org.org_id, photo.site_id, photo.sha256, photo.mime)).then((u) => { if (live) setUrl(u) }).catch(() => { /* offline, or not stored yet: the placeholder stays */ })
    }
    return () => { live = false; if (revoke) URL.revokeObjectURL(revoke) }
  }, [blob, photo.sha256, photo.mime, photo.site_id, transport, org.org_id])
  if (!url) return <span className={`${THUMB} flex items-center justify-center bg-slate-100 p-1 text-center text-[13px] text-slate-600 dark:bg-slate-800 dark:text-slate-300`} data-testid="history-photo-placeholder" data-sha={photo.sha256}>{t('photos.unavailable')}</span>
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={photo.caption ?? t('photos.alt')} className={THUMB} data-testid="history-photo" data-sha={photo.sha256} /></a>
}
